import { formatDateInterval, IDayInterval } from "@components/inputs/date/utils";
import { IEntity } from "@odata/BindingContext";
import { IPrAnnualTaxSettlementRequestEntity } from "@odata/GeneratedEntityTypes";
import { PrTaxDiscountTypeCode } from "@odata/GeneratedEnums";
import {
    TaxpayerDeclarationOverviewTableWrapper,
    TaxpayerDeclarationOverviewTitle
} from "@pages/payroll/annualTaxSettlementRequest/AnnualTaxSettlementRequest.styles";
import { IPrAnnualTaxSettlementRequestCustomData } from "@pages/payroll/annualTaxSettlementRequest/AnnualTaxSettlementRequestFormView";
import { isObjectEmpty } from "@utils/general";
import { capitalizeWords } from "@utils/string";
import React from "react";

import { DateFormat, getUtcDayjs } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";

interface IProps {
    storage: FormStorage<IEntity, IPrAnnualTaxSettlementRequestCustomData>;
}

interface IDiscountRow {
    key: string;
    name: React.ReactNode;
    validity: React.ReactNode;
}

function getValidityArr(includedMonths: string): IDayInterval[] {
    const validityArr: IDayInterval[] = [];
    let from = null;
    for (let i = 0; i < includedMonths.length; i++) {
        const val = includedMonths.charAt(i);
        if (val === "1" && from === null) {
            from = getUtcDayjs().set("month", i).toDate();
        } else if ((val === "0" || i === includedMonths.length - 1) && from !== null) {
            const to = getUtcDayjs().set("month", i - (val === "0" ? 1 : 0)).toDate();
            validityArr.push({ from, to });
            from = null;
        }
    }
    return validityArr;
}

export const TaxpayerDeclarationOverviewTable: React.FC<IProps> = ({ storage }) => {
    const entity = storage.getCustomData().annualTaxPayerInfo ?? storage.getEntity<IPrAnnualTaxSettlementRequestEntity>().YearlyTaxpayerDeclaration;
    const discounts = entity?.Discounts ?? [];
    const children = entity?.Children ?? [];

    const formatValidity = (value: IDayInterval): React.ReactNode => {
        return capitalizeWords(formatDateInterval(value, DateFormat.month));
    };


    const getDiscountRows = (): IDiscountRow[] => {
        const res: IDiscountRow[] = [];
        const taxPayerDiscount = discounts.find(d => d.DiscountTypeCode === PrTaxDiscountTypeCode.TaxPayerDiscount);
        if (!isObjectEmpty(taxPayerDiscount)) {
            // TaxPayerDiscount is always valid for the whole year, so we hardcode the validity here as it is not stored in the entity
            res.push({
                key: PrTaxDiscountTypeCode.TaxPayerDiscount,
                name: taxPayerDiscount.DiscountType.Name,
                validity: formatValidity({
                    from: getUtcDayjs().set("month", 0).toDate(),
                    to: getUtcDayjs().set("month", 11).toDate()
                })
            });
        }

        discounts.filter(d => d.DiscountTypeCode !== PrTaxDiscountTypeCode.TaxPayerDiscount).forEach(discount => {
            if (discount.IncludedMonths?.length) {
                const validityArr = getValidityArr(discount.IncludedMonths);
                validityArr.forEach((validity, index) => {
                    res.push({
                        key: discount.DiscountTypeCode + index,
                        name: index > 0 ? "" : discount.DiscountType.Name,
                        validity: formatValidity(validity)
                    });
                });
            }

        });

        children.forEach(c => {
            if (c.IncludedMonths?.length) {
                const validityArr = getValidityArr(c.IncludedMonths);
                validityArr.forEach((validity, index) => {
                    res.push({
                        key: c.Id + index + "",
                        name: `${storage.t("AnnualTaxSettlementRequest:Form.DiscountFor")} ${c.Order.Name} (${c.FirstName} ${c.LastName})`,
                        validity: formatValidity(validity)
                    });
                });
            }
        });

        return res;
    };

    if (getDiscountRows().length === 0) {
        return <TaxpayerDeclarationOverviewTitle>{storage.t("AnnualTaxSettlementRequest:Form.NothingIsApplied", { year: storage.getEntity<IPrAnnualTaxSettlementRequestEntity>().Year })}</TaxpayerDeclarationOverviewTitle>;
    }

    return <TaxpayerDeclarationOverviewTableWrapper>
        <TaxpayerDeclarationOverviewTitle>{storage.t("AnnualTaxSettlementRequest:Form.DeclarationOverviewTitle", { year: storage.getEntity<IPrAnnualTaxSettlementRequestEntity>().Year })}</TaxpayerDeclarationOverviewTitle>
        <table>
            <thead>
            <tr>
                <th>{storage.t("AnnualTaxSettlementRequest:Form.Type")}</th>
                <th>{storage.t("AnnualTaxSettlementRequest:Form.Period")}</th>
            </tr>
            </thead>
            <tbody>
            {getDiscountRows().map((row) => (<tr key={row.key}>
                <td>{row.name}</td>
                <td>{row.validity}</td>
            </tr>))}
            </tbody>
        </table>
    </TaxpayerDeclarationOverviewTableWrapper>;
};